import { useTranslation } from "react-i18next";
import formatPrice from "@/core/helpers/formatPrice";
import { Icon, Tooltip } from "@/shared/ui";
import { InfoState } from "@/domains/business/slice";
import getBusinessImage from "@/domains/business/helpers/getBusinessImage";

type Props = {
  info: InfoState;
};

export default function BusinessView({ info }: Props) {
  const { t } = useTranslation("business");

  const isSale = info && !info.owner;
  const url = info?.id ? getBusinessImage(info.id) : "";
  const paidDays = info ? Math.floor(info.feeBalance / info.totalFee) : 0;

  const tooltipContent = (
    <div className="absolute left-[-15.5rem] -top-[5rem] w-[43.2rem] px-4 py-2.5 bg-[#1E1E1E] border border-white/6 rounded-2xl">
      {t("business.businessBalanceDescription")}
    </div>
  );

  return (
    <div
      className="relative isolate rounded-[0.875rem] h-[15rem] shrink-0 overflow-clip bg-dark-800"
      style={{
        backgroundImage: `url(${url})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {isSale && (
        <div className="absolute flex items-center top-[2rem] -right-[3.5rem] h-[2.375rem] w-[13rem] py-1 bg-gradient-green rotate-45">
          <div className="h-full w-full flex item-center justify-center text-14 font-bold text-white uppercase border-t-2 border-b-2 border-dashed border-t-[#84FF97] border-b-[#84FF97]">
            {t("business.sale")}
          </div>
        </div>
      )}
      <div className="absolute bottom-2.5 left-0 w-full px-2.5 flex justify-between">
        <div className="relative h-11 flex items-center gap-2.5 px-3.5 rounded-2xl">
          <div className="absolute inset-0 rounded-2xl bg-dark-900/60 backdrop-blur-[5px]" />
          <Icon name="rub" className="relative z-1 size-5 shrink-0" />
          <div className="relative z-1 flex items-center gap-2.5">
            <div className="text-16 font-bold">{formatPrice(info?.expenseBalance)} ₽</div>
            <div className="w-px h-2.5 bg-white/20" />
            <div className="text-15 font-medium">{t("business.businessBalance")}</div>
            <div className="relative">
              <Tooltip content={tooltipContent}>
                <Icon
                  category="business"
                  name="info"
                  className="bg-white/50 size-4.5 transition-all duration-200 hover:bg-white"
                />
              </Tooltip>
            </div>
          </div>
        </div>
        <div className="relative h-11 flex items-center gap-2.5 px-3.5 rounded-2xl">
          <div
            aria-hidden
            className="absolute inset-0 rounded-2xl bg-dark-900/60 backdrop-blur-[5px]"
          />
          <Icon name="rent-time" className="relative z-1 size-5 shrink-0" />
          <div className="relative z-1 flex items-center gap-2.5">
            <div className="text-16 font-bold">{t("business.days", { count: paidDays })}</div>
            <div className="w-px h-2.5 bg-white/20" />
            <div className="text-15 font-medium">{t("business.rent")}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
